// 🚀 COMPREHENSIVE LOGGING SYSTEM - RAINBOW SAILS FRONTEND
// This utility provides centralized logging for all user interactions and system events

import { generateSessionId } from './sessionId';
import safeStorage from './safeStorage';
import { STORAGE_KEYS } from './storageKeys';

class Logger {
  constructor() {
    this.isEnabled = process.env.NODE_ENV === 'development' || safeStorage.getItem(STORAGE_KEYS.DEBUG_MODE, 'false') === 'true';
    this.sessionId = generateSessionId();
    this.startTime = Date.now();
    
    console.log('🚀 [LOGGER] Logging system initialized', {
      sessionId: this.sessionId,
      timestamp: new Date().toISOString(),
      enabled: this.isEnabled
    });
  }

  formatMessage(level, category, message, data = {}) {
    const timestamp = new Date().toISOString();
    const elapsed = Date.now() - this.startTime;
    
    return {
      sessionId: this.sessionId,
      timestamp,
      elapsed,
      level,
      category,
      message,
      data,
      url: window.location.href,
      userAgent: navigator.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`
    };
  }

  log(level, category, message, data = {}) {
    if (!this.isEnabled) return;
    
    const logEntry = this.formatMessage(level, category, message, data);
    const emoji = this.getEmoji(level, category);
    
    console.log(`${emoji} [${level.toUpperCase()}] [${category}] ${message}`, logEntry);
    
    // Store in localStorage for debugging
    this.storeLog(logEntry);
  }

  getEmoji(level, category) {
    const emojis = {
      'user': '👤',
      'system': '⚙️',
      'error': '❌',
      'warning': '⚠️',
      'success': '✅',
      'info': 'ℹ️',
      'navigation': '🧭',
      'button': '🔘',
      'form': '📝',
      'cart': '🛒',
      'product': '🎨',
      'auth': '🔐',
      'api': '🌐',
      'performance': '⚡',
      'analytics': '📊'
    };
    
    return emojis[category] || emojis[level] || '📝';
  }

  storeLog(logEntry) {
    try {
      const logs = safeStorage.getJSON(STORAGE_KEYS.LOGS, []);
      logs.push(logEntry);
      
      // Keep only last 1000 logs to prevent memory issues
      if (logs.length > 1000) {
        logs.splice(0, logs.length - 1000);
      }
      
      safeStorage.setJSON(STORAGE_KEYS.LOGS, logs);
    } catch (error) {
      console.error('Failed to store log:', error);
    }
  }

  // User Interaction Logging
  logUserAction(action, element, data = {}) {
    this.log('info', 'user', `User ${action}`, {
      action,
      element: element?.tagName || element?.type || 'unknown',
      elementId: element?.id || element?.className || 'unknown',
      ...data
    });
  }

  logButtonClick(buttonName, data = {}) {
    this.log('info', 'button', `Button clicked: ${buttonName}`, data);
  }

  logNavigation(from, to, method = 'click') {
    this.log('info', 'navigation', `Navigation: ${from} → ${to}`, {
      from,
      to,
      method,
      timestamp: Date.now()
    });
  }

  logFormSubmission(formName, data = {}) {
    this.log('info', 'form', `Form submitted: ${formName}`, data);
  }

  logFormInput(fieldName, value, formName = 'unknown') {
    this.log('info', 'form', `Form input: ${fieldName}`, {
      fieldName,
      formName,
      valueLength: typeof value === 'string' ? value.length : 'non-string',
      hasValue: !!value
    });
  }

  // System Event Logging
  logComponentMount(componentName, props = {}) {
    this.log('info', 'system', `Component mounted: ${componentName}`, {
      componentName,
      propsKeys: Object.keys(props),
      propsCount: Object.keys(props).length
    });
  }

  logComponentUnmount(componentName) {
    this.log('info', 'system', `Component unmounted: ${componentName}`, {
      componentName
    });
  }

  logAPIRequest(endpoint, method, data = {}) {
    this.log('info', 'api', `API Request: ${method} ${endpoint}`, {
      endpoint,
      method,
      dataKeys: Object.keys(data),
      timestamp: Date.now()
    });
  }

  logAPIResponse(endpoint, method, status, data = {}) {
    const level = status >= 400 ? 'error' : 'success';
    this.log(level, 'api', `API Response: ${method} ${endpoint} - ${status}`, {
      endpoint,
      method,
      status,
      responseKeys: Object.keys(data),
      timestamp: Date.now()
    });
  }

  logError(error, context = '') {
    this.log('error', 'error', `Error: ${error.message}`, {
      error: error.message,
      stack: error.stack,
      context,
      timestamp: Date.now()
    });
  }

  // Cart Operations
  logCartAction(action, productId, productName, data = {}) {
    this.log('info', 'cart', `Cart ${action}: ${productName}`, {
      action,
      productId,
      productName,
      ...data
    });
  }

  // Product Interactions
  logProductView(productId, productName, data = {}) {
    this.log('info', 'product', `Product viewed: ${productName}`, {
      productId,
      productName,
      ...data
    });
  }

  logProductInteraction(action, productId, productName, data = {}) {
    this.log('info', 'product', `Product ${action}: ${productName}`, {
      action,
      productId,
      productName,
      ...data
    });
  }

  // Authentication
  logAuthAction(action, data = {}) {
    this.log('info', 'auth', `Auth ${action}`, data);
  }

  // Performance Monitoring
  logPerformance(metric, value, context = '') {
    this.log('info', 'performance', `Performance: ${metric} = ${value}ms`, {
      metric,
      value,
      context,
      timestamp: Date.now()
    });
  }

  // Analytics
  logAnalytics(event, data = {}) {
    this.log('info', 'analytics', `Analytics: ${event}`, {
      event,
      ...data,
      timestamp: Date.now()
    });
  }

  // Utility Methods
  enable() {
    this.isEnabled = true;
    safeStorage.setItem(STORAGE_KEYS.DEBUG_MODE, 'true');
    console.log('🚀 [LOGGER] Logging enabled');
  }

  disable() {
    this.isEnabled = false;
    safeStorage.setItem(STORAGE_KEYS.DEBUG_MODE, 'false');
    console.log('🚀 [LOGGER] Logging disabled');
  }

  getLogs() {
    return safeStorage.getJSON(STORAGE_KEYS.LOGS, []);
  }

  clearLogs() {
    safeStorage.removeItem(STORAGE_KEYS.LOGS);
    console.log('🚀 [LOGGER] Logs cleared');
  }

  exportLogs() {
    const logs = this.getLogs();
    const dataStr = JSON.stringify(logs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rainbow-sails-logs-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}

// Create singleton instance
const logger = new Logger();

// Global error handler
window.addEventListener('error', (event) => {
  logger.logError(event.error, 'Global Error Handler');
});

// Global unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  logger.logError(new Error(event.reason), 'Unhandled Promise Rejection');
});

// Make logger available globally for debugging
window.rainbowLogger = logger;

export default logger;

